<template>
  <div class="news-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <h1 class="hero-title">Latest News & Updates</h1>
        <p class="hero-subtitle">
          Stay up-to-date with the latest developments, features, and announcements from eClub
        </p>
      </div>
    </section>

    <!-- News Content -->
    <section class="news-content">
      <div class="container">
        <div id="news-pulses-container" class="news-grid"></div>
      </div>
    </section>
  </div>
</template>

<script>
import { NewsPulsesLoader } from './news-pulses-loader.js'

export default {
  name: 'OfficialNews',
  mounted() {
    this.loadNewsLoader()
  },
  methods: {
    loadNewsLoader() {
      // Load marked.js library for markdown rendering
      const markedScript = document.createElement('script')
      markedScript.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js'
      markedScript.onload = () => {
        this.initializeNewsLoader()
      }
      document.head.appendChild(markedScript)
    },
    initializeNewsLoader() {
      const newsLoader = new NewsPulsesLoader({
        containerId: 'news-pulses-container',
        apiUrl: 'https://web-backend.knowit.today/Yousha/index.php',
        userId: '6951172f94c78494538b46a4',
        function: 'getUserPulses'
      })
      newsLoader.init()
    }
  }
}
</script>

<style scoped lang="scss">
.news-page {
  .hero {
    background: linear-gradient(135deg, #fff5f3 0%, #ffe6e3 100%);
    padding: 60px 0;
    text-align: center;

    .hero-title {
      font-size: 3rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 15px;
      line-height: 1.2;
    }

    .hero-subtitle {
      font-size: 1.2rem;
      color: #666;
      max-width: 700px;
      margin: 0 auto;
      line-height: 1.6;
    }
  }

  .news-content {
    padding: 60px 0;
    background: white;
    min-height: 400px;
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

// News grid styling
:deep(.news-grid) {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 20px;

  // Center placeholders when they are the only child
  &:has(.loading-news),
  &:has(.error-news) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

// News article card styling
:deep(.news-article) {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  .news-content-wrapper {
    padding: 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .news-meta {
    margin-bottom: 16px;
    font-size: 0.9rem;

    .news-date {
      color: #FF6B4A;
      font-weight: 600;
    }

    .news-updated {
      color: #999;
      font-size: 0.85rem;
      margin-left: 12px;
    }
  }

  .markdown-content {
    color: #333;
    line-height: 1.7;
    flex: 1;

    h1, h2, h3, h4, h5, h6 {
      color: #1a1a1a;
      margin-top: 16px;
      margin-bottom: 12px;
      font-weight: 600;

      &:first-child {
        margin-top: 0;
      }
    }

    h1 { font-size: 1.8rem; }
    h2 { font-size: 1.5rem; }
    h3 { font-size: 1.3rem; }

    p {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    ul, ol {
      margin: 12px 0;
      padding-left: 24px;

      li {
        margin-bottom: 8px;
      }
    }

    a {
      color: #FF6B4A;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    code {
      background: #f5f5f5;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    pre {
      background: #f5f5f5;
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin: 12px 0;

      code {
        background: transparent;
        padding: 0;
      }
    }

    blockquote {
      border-left: 4px solid #FF6B4A;
      padding-left: 16px;
      margin: 16px 0;
      color: #666;
      font-style: italic;
    }
  }

  .pulse-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;

    .pulse-tag {
      background: #fff5f3;
      color: #FF6B4A;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 0.85rem;
      font-weight: 500;
    }
  }

  .news-image-wrapper {
    position: relative;
    width: 100%;
    height: 200px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    &:hover img {
      transform: scale(1.05);
    }

    .image-count-badge {
      position: absolute;
      bottom: 12px;
      right: 12px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 500;
    }
  }
}

// Loading state
:deep(.loading-news) {
  text-align: center;
  padding: 60px 20px;

  .loading-spinner {
    width: 50px;
    height: 50px;
    margin: 0 auto 20px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #FF6B4A;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    color: #666;
    font-size: 1.1rem;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Error state
:deep(.error-news) {
  text-align: center;
  padding: 60px 20px;
  color: #666;

  p {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }

  .error-details {
    color: #999;
    font-size: 0.95rem;
  }

  .btn {
    margin-top: 20px;
    display: inline-block;
    padding: 12px 24px;
    background: #FF6B4A;
    color: white;
    border: none;
    border-radius: 25px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #e55a41;
    }
  }
}

// Modal styling
:deep(.news-modal) {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;

  &.active {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 40px;
  }

  .news-modal-content {
    background: white;
    border-radius: 16px;
    max-width: 800px;
    width: 100%;
    margin: 0 auto 40px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: modalSlideIn 0.3s ease;
  }

  .news-modal-header {
    padding: 24px 24px 16px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .news-date {
      color: #FF6B4A;
      font-weight: 600;
      font-size: 1rem;
    }

    .news-updated {
      display: block;
      color: #999;
      font-size: 0.85rem;
      margin-top: 4px;
    }

    .news-modal-close {
      background: transparent;
      border: none;
      font-size: 2rem;
      color: #999;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.3s ease;

      &:hover {
        background: #f5f5f5;
        color: #333;
      }
    }
  }

  .news-modal-body {
    padding: 24px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;

    .markdown-content {
      color: #333;
      line-height: 1.7;
      font-size: 1.05rem;

      h1, h2, h3, h4, h5, h6 {
        color: #1a1a1a;
        margin-top: 24px;
        margin-bottom: 16px;
        font-weight: 600;

        &:first-child {
          margin-top: 0;
        }
      }

      h1 { font-size: 2rem; }
      h2 { font-size: 1.7rem; }
      h3 { font-size: 1.4rem; }

      p {
        margin-bottom: 16px;
      }

      ul, ol {
        margin: 16px 0;
        padding-left: 28px;

        li {
          margin-bottom: 12px;
        }
      }

      a {
        color: #FF6B4A;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        margin: 16px 0;
      }

      code {
        background: #f5f5f5;
        padding: 3px 8px;
        border-radius: 4px;
        font-family: 'Courier New', monospace;
        font-size: 0.95em;
      }

      pre {
        background: #f5f5f5;
        padding: 20px;
        border-radius: 8px;
        overflow-x: auto;
        margin: 20px 0;

        code {
          background: transparent;
          padding: 0;
        }
      }

      blockquote {
        border-left: 4px solid #FF6B4A;
        padding-left: 20px;
        margin: 20px 0;
        color: #666;
        font-style: italic;
      }
    }

    .pulse-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin: 24px 0;

      .pulse-tag {
        background: #fff5f3;
        color: #FF6B4A;
        padding: 6px 16px;
        border-radius: 16px;
        font-size: 0.9rem;
        font-weight: 500;
      }
    }

    .news-modal-images {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 16px;
      margin-top: 24px;

      img {
        width: 100%;
        height: 200px;
        object-fit: cover;
        border-radius: 8px;
        cursor: pointer;
        transition: transform 0.3s ease;

        &:hover {
          transform: scale(1.02);
        }
      }
    }
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive design
@media (max-width: 768px) {
  .news-page {
    .hero {
      padding: 40px 0;

      .hero-title {
        font-size: 2rem;
      }

      .hero-subtitle {
        font-size: 1rem;
      }
    }
  }

  :deep(.news-grid) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  :deep(.news-modal) {
    padding: 10px;

    .news-modal-content {
      margin: 0 auto 20px;
    }

    .news-modal-body {
      max-height: calc(100vh - 150px);
    }
  }
}
</style>
